import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { Unsubscribe } from 'router5/dist/types/base';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IAlertsService } from '../../infrastructure/alerts';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { AccountGroupsForm } from './_account-groups-form/account-groups-form';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { IAccountGroupDTO, IAccountGroupsDAO, IAccountGroupsListDTOInternal } from '../../api/account-groups.dao';

class AccountGroupsVM implements IModuleVM {
    @mobx.observable
    public accountGroupsList: IAccountGroupDTO[];
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public groupIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    private subscriptions: Unsubscribe[];
    @mobx.observable
    public loadMorePaginationToken: string | null;

    @mobx.observable
    public paginationVM: PagingViewModel;

    constructor(
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly accountsGroupsDAO: IAccountGroupsDAO = IoC.Container.get(IAccountGroupsDAO)
    ) {
        this.groupIdToSubWindowMap = {};
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchAccountGroupsList);
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(Routes.AccountGroupsList, async () => {
            this.fetchAccountGroupsList();
        }));
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchAccountGroupsList = async () => {
        mobx.runInAction(() => {
            this.isLoading = true;
        });

        try {
            const params = {
                page: this.paginationVM.getCurrentPageForBE(),
                size: this.paginationVM.getPageSize()
            };

            const response = await this.accountsGroupsDAO.getAccountGroups(params);
            this.onFetchAccountGroupsListSuccess(response);
        } catch (error) {
            this.onFetchAccountGroupsListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchAccountGroupsListSuccess(response: IAccountGroupsListDTOInternal): void {
        this.accountGroupsList = response.content;

        this.paginationVM.setTotalRecords(response.totalElements);
    }

    @mobx.action
    private onFetchAccountGroupsListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error),
        });
    }

    public showCreateGroupForm = (intl: IntlShape): void => {
        this.showGroupForm({
            title: intl.formatMessage({
                id: `oe.accountGroups.newAccountGroup`,
                description: 'New Account Group button',
                defaultMessage: 'New Account Group',
            }),
        });
    };

    public showGroupForm = params => {
        const { title, accountGroupId } = params;
        return this.subWindowService.showSubWindow({
            componentToHost: AccountGroupsForm,
            componentToHostProps: {
                fetchAccountGroupsList: this.fetchAccountGroupsList,
                closeEditGroupForm: this.closeSubWindow,
                accountGroupId: accountGroupId,
            },
            title: title,
        });
    };

    public editGroup = (intl: IntlShape, accountGroupId: number): void => {
        let shallShowNewEditWindow = true;
        const alreadyShownSubWindow = this.groupIdToSubWindowMap[accountGroupId];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            mobx.runInAction(() => {
                this.groupIdToSubWindowMap[accountGroupId] = this.showGroupForm({
                    title: intl.formatMessage({
                        id: `oe.accountGroups.editAccountGroup`,
                        description: 'Edit Account Group button',
                        defaultMessage: 'Edit Group',
                    }),
                    accountGroupId: accountGroupId,
                    closeEditGroupForm: this.closeSubWindow,
                });
            });
        }
    };

    public isGroupFormShown = (accountGroupId: string): boolean => {
        return (
            this.groupIdToSubWindowMap[accountGroupId] &&
            this.groupIdToSubWindowMap[accountGroupId].getStatus() !== SubWindowStatus.Closed
        );
    };

    public closeSubWindow = (accountGroupId: number) => {
        this.groupIdToSubWindowMap[accountGroupId.toString()].close();
    };
}

export { AccountGroupsVM };
